import { ArrowLeftOutlined } from '@ant-design/icons';
import { App, Button, Result, Spin, Tag } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import TabbedPage, { TabbedPageTab } from 'components/tabbed-page';
import { getEvent } from './api';
import { GalaEventDetail, STATUS_META, errorText } from './contract';
import BlocksTab from './tabs/blocks-tab';
import CategoriesTab from './tabs/categories-tab';
import CheckinTab from './tabs/checkin-tab';
import InfoTab from './tabs/info-tab';
import NominationsTab from './tabs/nominations-tab';
import RemindersTab from './tabs/reminders-tab';
import SeatingTab from './tabs/seating-tab';
import TicketsTab from './tabs/tickets-tab';
import styles from './styles.module.scss';

/** Trang một sự kiện Gala: 8 tab (docs/gala/01-dac-ta-2026-10-13.md §8, Chỗ ngồi: docs/gala/04 §7, Đề cử & giải: docs/gala/09). */
const GalaEventPage = () => {
  const { message } = App.useApp();
  const { id = '' } = useParams();
  const eventId = Number(id);
  const [detail, setDetail] = useState<GalaEventDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState<string | null>(null);
  const requestRef = useRef(0);

  // Tải lại im lặng (không bật vòng xoay) sau mỗi lần lưu để form đang mở không bị dựng lại.
  const reload = useCallback(
    async (silent = true) => {
      const ticket = ++requestRef.current;
      if (!silent) setLoading(true);
      try {
        const res = await getEvent(eventId);
        if (ticket !== requestRef.current) return;
        setDetail(res);
        setFailed(null);
      } catch (e) {
        if (ticket !== requestRef.current) return;
        if (silent) message.error(errorText(e, 'Không tải lại được sự kiện.'));
        else setFailed(errorText(e, 'Không tải được sự kiện Gala.'));
      } finally {
        if (ticket === requestRef.current) setLoading(false);
      }
    },
    [eventId, message]
  );

  useEffect(() => {
    if (!Number.isInteger(eventId) || eventId <= 0) {
      setFailed('Đường dẫn sự kiện không hợp lệ.');
      setLoading(false);
      return undefined;
    }
    reload(false);
    return () => {
      requestRef.current += 1; // bỏ kết quả đang bay khi đổi sự kiện / rời trang
    };
  }, [eventId, reload]);

  const back = (
    <Link to="/gala">
      <Button icon={<ArrowLeftOutlined />}>Danh sách sự kiện</Button>
    </Link>
  );

  if (loading && !detail) {
    return (
      <div className={styles.placeholder}>
        <Spin />
      </div>
    );
  }

  if (!detail) {
    return (
      <Result
        status="warning"
        title="Không mở được sự kiện"
        subTitle={failed ?? 'Sự kiện không tồn tại hoặc đã bị xoá.'}
        extra={back}
      />
    );
  }

  const { event } = detail;
  const status = STATUS_META[event.status];
  const tabs: TabbedPageTab[] = [
    {
      key: 'info',
      label: 'Thông tin',
      render: () => <InfoTab event={event} categories={detail.categories} onChanged={reload} />,
    },
    {
      key: 'blocks',
      label: 'Nội dung',
      render: () => <BlocksTab eventId={event.id} blocks={detail.blocks} onChanged={reload} />,
    },
    {
      key: 'categories',
      label: 'Hạng mục',
      render: () => (
        <CategoriesTab eventId={event.id} categories={detail.categories} onChanged={reload} />
      ),
    },
    {
      key: 'nominations',
      label: 'Đề cử & giải',
      render: () => <NominationsTab eventId={event.id} eventStatus={event.status} />,
    },
    { key: 'tickets', label: 'Vé mời', render: () => <TicketsTab eventId={event.id} /> },
    { key: 'seating', label: 'Chỗ ngồi', render: () => <SeatingTab eventId={event.id} /> },
    { key: 'checkin', label: 'Check-in', render: () => <CheckinTab eventId={event.id} /> },
    { key: 'reminders', label: 'Nhắc lịch', render: () => <RemindersTab eventId={event.id} /> },
  ];

  return (
    <TabbedPage
      tabs={tabs}
      title={event.title}
      extra={
        <div className={styles.headExtra}>
          <Tag color={status?.color}>{status?.text ?? event.status}</Tag>
          <strong>{event.title}</strong>
          {back}
        </div>
      }
    />
  );
};

export default GalaEventPage;
